/**
 * V2.1.0 Tags Details tree: accordion title is "Level1 / Level2", chips are Level3.
 */

export type CategoryTagSection = {
  sectionTitle: string;
  level3Items: unknown[];
};

function readName(node: unknown): string {
  if (typeof node === "string") return node;
  if (node && typeof node === "object" && "name" in node) {
    const name = (node as { name?: unknown }).name;
    return typeof name === "string" ? name : "";
  }
  return "";
}

function readChildren(node: unknown): unknown[] {
  if (!node || typeof node !== "object") return [];
  const record = node as Record<string, unknown>;
  const raw =
    record.sub_categories ??
    record.sub_markets ??
    record.sub_items ??
    record.children;
  return Array.isArray(raw) ? raw : [];
}

export function categoryTagItemLabel(item: unknown): string {
  return readName(item);
}

/** Backend placeholder node. It is not a tag; the summary chips already skip it. */
function isSyntheticAll(node: unknown): boolean {
  return readName(node) === "All";
}

/** Flatten a categories/tags tree the same way CategoriesViewAllDialog does. */
export function buildCategoryTagSections(
  items: readonly unknown[] | null | undefined,
): CategoryTagSection[] {
  if (!items || items.length === 0) return [];

  const sections: CategoryTagSection[] = [];
  const visitLevel1 = (level1: unknown) => {
    if (isSyntheticAll(level1)) {
      for (const child of readChildren(level1)) visitLevel1(child);
      return;
    }

    const l1Name = readName(level1);
    const level2List = readChildren(level1);
    if (level2List.length === 0) {
      if (!l1Name) return;
      sections.push({ sectionTitle: l1Name, level3Items: [] });
      return;
    }
    for (const level2 of level2List) {
      if (isSyntheticAll(level2)) {
        const level3Items = readChildren(level2);
        if (level3Items.length === 0) continue;
        sections.push({ sectionTitle: l1Name, level3Items });
        continue;
      }
      sections.push({
        sectionTitle: `${l1Name} / ${readName(level2)}`,
        level3Items: readChildren(level2),
      });
    }
  };

  for (const level1 of items) visitLevel1(level1);
  return sections;
}

export function filterCategoryTagSections(
  sections: readonly CategoryTagSection[],
  searchTerm: string,
): CategoryTagSection[] {
  const term = searchTerm.trim().toLowerCase();
  if (!term) return [...sections];

  const filtered: CategoryTagSection[] = [];
  for (const section of sections) {
    const titleMatches = section.sectionTitle.toLowerCase().includes(term);
    const matchedLeaves = section.level3Items.filter((item) =>
      readName(item).toLowerCase().includes(term),
    );
    if (!titleMatches && matchedLeaves.length === 0) continue;
    filtered.push({
      ...section,
      level3Items: titleMatches ? [...section.level3Items] : matchedLeaves,
    });
  }
  return filtered;
}
